import { useState, useMemo, useEffect } from "react";
import { IMPORTED_DRAFTS } from "@/data/importedDrafts";
import { loadLinkedStatus } from "@/services/importedDrafts";
import LinkUserModal from "./LinkUserModal";
import {
  Search,
  ChevronDown,
  ChevronUp,
  Mail,
  Calendar,
  Activity,
  User,
  FileText,
  X,
  Link,
  CheckCircle,
  Loader2,
  Upload,
} from "lucide-react";

// ── Sub-componente: tarjeta de medición individual ──────────────────────────
function MeasurementCard({ m, index }) {
  const fields = [
    { label: "Peso", value: m.peso, unit: "kg" },
    { label: "Talla", value: m.talla, unit: "cm" },
    { label: "IMC", value: m.imc, unit: "" },
    { label: "Cintura", value: m.cintura, unit: "cm" },
    {
      label: "PA",
      value:
        m.paSistolica && m.paDiastolica
          ? `${m.paSistolica}/${m.paDiastolica}`
          : null,
      unit: "mmHg",
    },
    { label: "Glucemia", value: m.glucemia, unit: "mg/dl" },
    { label: "FC", value: m.fc, unit: "lpm" },
    { label: "SpO2", value: m.oximetria, unit: "%" },
  ].filter((f) => f.value != null);

  return (
    <div
      className="rounded-lg border p-3"
      style={{
        borderColor: index === 0 ? "#A7F3D0" : "#E2E8F0",
        background: index === 0 ? "#F0FDF4" : "#FAFAFA",
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            className="text-xs font-semibold"
            style={{ color: index === 0 ? "#166534" : "#64748B" }}
          >
            {m.source}
          </span>
          {index === 0 && (
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded"
              style={{ background: "#DCFCE7", color: "#166534" }}
            >
              M&aacute;s reciente
            </span>
          )}
        </div>
        {m.date && (
          <span className="text-xs" style={{ color: "#94A3B8" }}>
            <Calendar size={10} className="inline mr-1" />
            {m.date}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {fields.map((f) => (
          <span key={f.label} className="text-xs" style={{ color: "#475569" }}>
            <span className="font-medium">{f.label}:</span> <span>{f.value}</span>
            {f.unit && <span style={{ color: "#94A3B8" }}> {f.unit}</span>}
          </span>
        ))}
      </div>
      {m.condicionTexto && (
        <p className="text-xs mt-1.5" style={{ color: "#92400E" }}>
          <Activity size={10} className="inline mr-1" />
          {m.condicionTexto}
        </p>
      )}
    </div>
  );
}

// ── Sub-componente: fila de persona ─────────────────────────────────────────
function PersonRow({ person, linkStatus, onVincular }) {
  const [expanded, setExpanded] = useState(false);
  const latest = person.latestMeasurement || {};
  const hasMultiple = person.totalMeasurements > 1;
  const linked = linkStatus?.status === "linked" && linkStatus?.linkedUid;

  return (
    <div
      className="border-b last:border-b-0"
      style={{ borderColor: "#F1F5F9" }}
      data-testid={`draft-row-${person.id}`}
    >
      {/* Summary row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Avatar */}
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-xs font-bold cursor-pointer"
          style={{
            background:
              person.sexo === "Femenino"
                ? "#FCE7F3"
                : person.sexo === "Masculino"
                ? "#DBEAFE"
                : "#F1F5F9",
            color:
              person.sexo === "Femenino"
                ? "#BE185D"
                : person.sexo === "Masculino"
                ? "#1D4ED8"
                : "#64748B",
          }}
          onClick={() => setExpanded(!expanded)}
        >
          {(person.nombres[0] || "").toUpperCase()}
          {(person.apellidos[0] || "").toUpperCase()}
        </div>

        {/* Name + metadata */}
        <div
          className="flex-1 min-w-0 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-2">
            <span
              className="font-medium text-sm truncate"
              style={{ color: "#1E293B" }}
            >
              {person.nombres} {person.apellidos}
            </span>
            {linked && (
              <CheckCircle size={13} style={{ color: "#16A34A", flexShrink: 0 }} />
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            {person.fechaNacimiento && (
              <span className="text-[11px]" style={{ color: "#94A3B8" }}>
                FN: {person.fechaNacimiento}
              </span>
            )}
            {person.sexo && (
              <span className="text-[11px]" style={{ color: "#94A3B8" }}>
                {person.sexo}
              </span>
            )}
            {linked && (
              <span className="text-[11px]" style={{ color: "#16A34A" }}>
                {linkStatus.linkedEmail}
              </span>
            )}
          </div>
        </div>

        {/* Measurements count */}
        <span
          className="text-xs font-semibold px-2 py-1 rounded-full shrink-0"
          style={{
            background: hasMultiple ? "#EFF6FF" : "#F1F5F9",
            color: hasMultiple ? "#1D4ED8" : "#64748B",
          }}
        >
          {person.totalMeasurements} med.
        </span>

        {/* Quick stats */}
        <div className="hidden lg:flex items-center gap-3 shrink-0">
          {latest.paSistolica && (
            <span className="text-xs" style={{ color: "#64748B" }}>
              {latest.paSistolica}/{latest.paDiastolica}
            </span>
          )}
          {latest.imc && (
            <span className="text-xs" style={{ color: "#64748B" }}>
              IMC {latest.imc}
            </span>
          )}
        </div>

        {/* Vincular button */}
        <div className="shrink-0">
          {linked ? (
            <span
              className="text-[11px] px-2 py-1 rounded-full font-semibold flex items-center gap-1"
              style={{ background: "#DCFCE7", color: "#166534" }}
              data-testid={`draft-linked-badge-${person.id}`}
            >
              <CheckCircle size={11} /> Vinculado
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onVincular(person)}
              className="text-[11px] px-2.5 py-1.5 rounded-lg font-semibold flex items-center gap-1 transition-all active:scale-95"
              style={{
                background: "#EFF6FF",
                color: "#1D4ED8",
                border: "1px solid #BFDBFE",
              }}
              data-testid={`draft-vincular-btn-${person.id}`}
            >
              <Link size={11} /> Vincular
            </button>
          )}
        </div>

        {/* Expand toggle */}
        <div className="shrink-0 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          {expanded ? (
            <ChevronUp size={16} style={{ color: "#94A3B8" }} />
          ) : (
            <ChevronDown size={16} style={{ color: "#94A3B8" }} />
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 space-y-2" style={{ background: "#FAFAFA" }}>
          {linked && (
            <div
              className="flex items-center gap-2 py-2 px-3 rounded-lg text-xs mb-2"
              style={{ background: "#F0FDF4", color: "#166534", border: "1px solid #BBF7D0" }}
            >
              <CheckCircle size={12} />
              <span>
                Vinculado a <strong>{linkStatus.linkedName || linkStatus.linkedEmail}</strong>
                {linkStatus.measurementsWritten > 0 && (
                  <> · {linkStatus.measurementsWritten} medición(es) escritas</>
                )}
              </span>
            </div>
          )}
          <p className="text-xs font-semibold pt-1" style={{ color: "#475569" }}>
            Historial de mediciones ({person.totalMeasurements})
          </p>
          {person.measurements.map((m, i) => (
            <MeasurementCard key={i} m={m} index={i} />
          ))}
          {person.evaluacion && (
            <div
              className="rounded-lg border p-2 text-xs"
              style={{ borderColor: "#FCD34D", background: "#FFFBEB", color: "#92400E" }}
            >
              <FileText size={10} className="inline mr-1" />
              <span className="font-medium">Evaluaci&oacute;n:</span> {person.evaluacion}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Componente principal ────────────────────────────────────────────────────
export default function AdminImport() {
  const [search, setSearch] = useState("");
  const [filterSex, setFilterSex] = useState("all");
  const [filterLinked, setFilterLinked] = useState("all"); // all | linked | pending
  const [filterMultiple, setFilterMultiple] = useState(false);

  // Estado de vínculos cargado desde Firestore
  const [linkMap, setLinkMap] = useState({}); // { draftId: linkDoc }
  const [loadingLinks, setLoadingLinks] = useState(true);

  // Modal
  const [modalDraft, setModalDraft] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Cargar vínculos existentes al montar
  useEffect(() => {
    loadLinkedStatus()
      .then((map) => setLinkMap(map))
      .catch(() => {}) // silencioso si la colección no existe aún
      .finally(() => setLoadingLinks(false));
  }, []);

  const linkedCount = Object.values(linkMap).filter(
    (l) => l.status === "linked"
  ).length;
  const pendingCount = IMPORTED_DRAFTS.length - linkedCount;

  const drafts = useMemo(() => {
    let list = [...IMPORTED_DRAFTS];
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((p) =>
        `${p.nombres} ${p.apellidos}`.toLowerCase().includes(s)
      );
    }
    if (filterSex !== "all") {
      list = list.filter((p) =>
        filterSex === "sin" ? !p.sexo : p.sexo === filterSex
      );
    }
    if (filterLinked === "linked") {
      list = list.filter((p) => linkMap[p.id]?.status === "linked");
    } else if (filterLinked === "pending") {
      list = list.filter((p) => !linkMap[p.id] || linkMap[p.id]?.status !== "linked");
    }
    if (filterMultiple) {
      list = list.filter((p) => p.totalMeasurements > 1);
    }
    return list;
  }, [search, filterSex, filterLinked, filterMultiple, linkMap]);

  const totalMeasurements = IMPORTED_DRAFTS.reduce(
    (acc, p) => acc + p.totalMeasurements,
    0
  );

  function handleLinked({ draftId, linkedUid, linkedEmail, linkedName, measurementsWritten }) {
    setLinkMap((prev) => ({
      ...prev,
      [draftId]: { status: "linked", linkedUid, linkedEmail, linkedName, measurementsWritten },
    }));
    setModalDraft(null);
    setSuccessMsg(
      `Vinculado correctamente. ${measurementsWritten} medición(es) escritas en el historial.`
    );
    setTimeout(() => setSuccessMsg(null), 5000);
  }

  return (
    <div>
      {/* Success banner */}
      {successMsg && (
        <div
          className="flex items-center gap-2 px-4 py-3 rounded-xl mb-4 text-sm"
          style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#166534" }}
          data-testid="import-success-banner"
        >
          <CheckCircle size={16} />
          {successMsg}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div
          className="bg-white rounded-xl p-4 border border-gray-100"
          data-testid="import-stat-people"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: "#E0F2FE" }}
            >
              <User size={20} style={{ color: "#0284C7" }} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: "#1E293B" }}>
                {IMPORTED_DRAFTS.length}
              </p>
              <p className="text-xs" style={{ color: "#64748B" }}>Personas</p>
            </div>
          </div>
        </div>
        <div
          className="bg-white rounded-xl p-4 border border-gray-100"
          data-testid="import-stat-measurements"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: "#F3E8FF" }}
            >
              <Activity size={20} style={{ color: "#7C3AED" }} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: "#1E293B" }}>
                {totalMeasurements}
              </p>
              <p className="text-xs" style={{ color: "#64748B" }}>Mediciones</p>
            </div>
          </div>
        </div>
        <div
          className="bg-white rounded-xl p-4 border border-gray-100"
          data-testid="import-stat-linked"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: linkedCount > 0 ? "#DCFCE7" : "#FEF3C7" }}
            >
              <CheckCircle
                size={20}
                style={{ color: linkedCount > 0 ? "#16A34A" : "#D97706" }}
              />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: "#1E293B" }}>
                {loadingLinks ? "…" : linkedCount}
              </p>
              <p className="text-xs" style={{ color: "#64748B" }}>Vinculados</p>
            </div>
          </div>
        </div>
        <div
          className="bg-white rounded-xl p-4 border border-gray-100"
          data-testid="import-stat-pending"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: "#FEE2E2" }}
            >
              <Mail size={20} style={{ color: "#DC2626" }} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: "#1E293B" }}>
                {loadingLinks ? "…" : pendingCount}
              </p>
              <p className="text-xs" style={{ color: "#64748B" }}>Pendientes</p>
            </div>
          </div>
        </div>
      </div>

      {/* TODO: GCP Sync — botón deshabilitado, pendiente de mantenimiento del gateway */}
      <div
        className="flex items-center justify-between px-4 py-3 rounded-xl border mb-4"
        style={{ background: "#FFFBEB", borderColor: "#FDE68A" }}
        data-testid="import-gcp-todo-banner"
      >
        <div className="flex items-center gap-2">
          <Upload size={15} style={{ color: "#D97706" }} />
          <div>
            <p className="text-xs font-semibold" style={{ color: "#92400E" }}>
              Sincronización con GCP
            </p>
            <p className="text-[11px]" style={{ color: "#B45309" }}>
              TODO: Disponible cuando el gateway salga de mantenimiento
            </p>
          </div>
        </div>
        <button
          type="button"
          disabled
          className="text-xs px-3 py-1.5 rounded-lg font-semibold"
          style={{ background: "#FEF3C7", color: "#B45309", cursor: "not-allowed", border: "1px solid #FDE68A" }}
        >
          Exportar para GCP
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: "#94A3B8" }}
          />
          <input
            type="text"
            placeholder="Buscar por nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm"
            style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
            data-testid="import-search"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X size={14} style={{ color: "#94A3B8" }} />
            </button>
          )}
        </div>
        <select
          value={filterSex}
          onChange={(e) => setFilterSex(e.target.value)}
          className="px-3 py-2.5 rounded-lg border text-sm"
          style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
          data-testid="import-filter-sex"
        >
          <option value="all">Todos</option>
          <option value="Femenino">Femenino</option>
          <option value="Masculino">Masculino</option>
          <option value="sin">Sin dato</option>
        </select>
        <select
          value={filterLinked}
          onChange={(e) => setFilterLinked(e.target.value)}
          className="px-3 py-2.5 rounded-lg border text-sm"
          style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
          data-testid="import-filter-linked"
        >
          <option value="all">Todos</option>
          <option value="linked">Vinculados</option>
          <option value="pending">Pendientes</option>
        </select>
        <button
          onClick={() => setFilterMultiple(!filterMultiple)}
          className="px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors"
          style={{
            borderColor: filterMultiple ? "#3B82F6" : "#E2E8F0",
            background: filterMultiple ? "#EFF6FF" : "white",
            color: filterMultiple ? "#1D4ED8" : "#64748B",
          }}
          data-testid="import-filter-multi"
        >
          Con historial
        </button>
      </div>

      {/* Results count */}
      <p className="text-xs mb-3" style={{ color: "#94A3B8" }}>
        Mostrando {drafts.length} de {IMPORTED_DRAFTS.length} personas
        {loadingLinks && (
          <span className="inline-flex items-center gap-1 ml-2">
            <Loader2 size={10} className="animate-spin" /> cargando vínculos…
          </span>
        )}
      </p>

      {/* List */}
      <div
        className="bg-white rounded-xl border border-gray-100 overflow-hidden"
        data-testid="import-list"
      >
        {drafts.length === 0 ? (
          <div className="text-center py-12" style={{ color: "#94A3B8" }}>
            No se encontraron personas con ese filtro
          </div>
        ) : (
          drafts.map((person) => (
            <PersonRow
              key={person.id}
              person={person}
              linkStatus={linkMap[person.id] || null}
              onVincular={setModalDraft}
            />
          ))
        )}
      </div>

      {/* Modal */}
      {modalDraft && (
        <LinkUserModal
          draft={modalDraft}
          onClose={() => setModalDraft(null)}
          onLinked={handleLinked}
        />
      )}
    </div>
  );
}
